/**
 * Local wishlist — saved in this browser's localStorage, no third-party service.
 *
 * Markup contract (product-card, product-card-featured, main-product, SearchTap):
 *   <button data-wishlist-button data-product-id="…" data-variant-id="…" data-product-handle="…">
 * Every button for a product mirrors its state through `aria-pressed` and the
 * `is-wishlisted` class. Clicks are delegated from the document and new buttons
 * are synced by a MutationObserver, so injected cards need no re-init.
 *
 * Some saved products aren't sold in every market (licensed products are UAE
 * only). They stay saved, but the count and the wishlist page skip them in markets
 * where the Storefront API can't see them — tracked per country and refreshed in
 * the background (one small request, only for unchecked ids).
 *
 * `[data-wishlist-count]` elements show the visible item count. Each change dispatches
 * `wishlist:change` ({ action, item, count, source }) on document — `source` is
 * the clicked button, absent for programmatic changes.
 */
import { ThemeEvents } from '@theme/events';
import { fetchAvailableProductIds } from '@theme/market-availability';

export const STORAGE_KEY = 'tss:wishlist';
const MAX_ITEMS = 200;
const BUTTON_SELECTOR = '[data-wishlist-button]';
const COUNT_SELECTOR = '[data-wishlist-count]';
const AVAILABILITY_TTL_MS = 12 * 60 * 60 * 1000;

/**
 * @typedef {Object} WishlistItem
 * @property {string} productId
 * @property {string} variantId
 * @property {string} handle
 * @property {number} addedAt
 */

/** @returns {WishlistItem[]} newest first */
function readItems() {
  try {
    const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(items) ? items.filter((item) => item && item.productId && item.handle) : [];
  } catch {
    return [];
  }
}

/** @param {WishlistItem[]} items */
function writeItems(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch (error) {
    console.error('[wishlist] could not save:', error);
  }
}

/* ------------------------------------------------- Market availability -- */

/** Storefront API settings, printed by snippets/scripts.liquid. */
const storefront = () => window.themeStorefront || {};
const availabilityKey = () => `${STORAGE_KEY}:unavailable:${storefront().country || 'default'}`;

/** @returns {{checked: string[], unavailable: string[], at: number}} for the current country */
function readAvailability() {
  try {
    const value = JSON.parse(localStorage.getItem(availabilityKey()) || 'null');
    if (value && Array.isArray(value.checked) && Array.isArray(value.unavailable)) return value;
  } catch {}
  return { checked: [], unavailable: [], at: 0 };
}

function writeAvailability(value) {
  try {
    localStorage.setItem(availabilityKey(), JSON.stringify(value));
  } catch {}
}

/** Record what the Storefront API could and couldn't see in this market. */
function recordAvailability(foundIds, missingIds) {
  const current = readAvailability();
  const found = new Set(foundIds.map(String));
  const missing = new Set(missingIds.map(String));
  writeAvailability({
    checked: [...new Set([...current.checked, ...found, ...missing])],
    unavailable: [...new Set([...current.unavailable.filter((id) => !found.has(id)), ...missing])],
    at: current.at || Date.now()
  });
}

/** Saved items sold in the current market. */
function visibleItems() {
  const unavailable = new Set(readAvailability().unavailable);
  return readItems().filter((item) => !unavailable.has(item.productId));
}

/** Ask the Storefront API about ids not yet checked in this market (or all, once stale). */
async function refreshAvailability() {
  const items = readItems();
  if (items.length === 0) return;

  const current = readAvailability();
  const isStale = Date.now() - current.at > AVAILABILITY_TTL_MS;
  const ids = items.map((item) => item.productId).filter((id) => isStale || !current.checked.includes(id));
  if (ids.length === 0) return;

  const available = await fetchAvailableProductIds(ids);
  if (!available) return;
  if (isStale) writeAvailability({ checked: [], unavailable: [], at: Date.now() });
  recordAvailability(
    [...available],
    ids.filter((id) => !available.has(id))
  );
  syncAll();
}

function announce(action, item, source) {
  syncAll();
  document.dispatchEvent(new CustomEvent(ThemeEvents.wishlistChange, { detail: { action, item, count: visibleItems().length, source } }));
}

export const wishlist = {
  /** @returns {WishlistItem[]} */
  items: () => readItems(),

  /** @returns {WishlistItem[]} saved items sold in the current market */
  visibleItems: () => visibleItems(),

  count: () => visibleItems().length,

  /** The wishlist page reports what it could and couldn't show in this market. */
  recordAvailability(foundIds, missingIds) {
    recordAvailability(foundIds, missingIds);
    syncAll();
  },

  /** @param {string|number} productId */
  has: (productId) => readItems().some((item) => item.productId === String(productId)),

  /**
   * @param {{productId: string|number, variantId?: string|number, handle: string}} entry
   * @param {Element} [source]
   */
  add(entry, source) {
    const productId = String(entry.productId || '');
    if (!productId || !entry.handle) return false;

    const items = readItems().filter((item) => item.productId !== productId);
    const item = { productId, variantId: String(entry.variantId || ''), handle: entry.handle, addedAt: Date.now() };
    writeItems([item, ...items]);
    // Hearted here, so it's sold in this market.
    recordAvailability([productId], []);
    announce('add', item, source);
    return true;
  },

  /**
   * @param {string|number} productId
   * @param {Element} [source]
   */
  remove(productId, source) {
    const items = readItems();
    const item = items.find((candidate) => candidate.productId === String(productId));
    if (!item) return false;

    writeItems(items.filter((candidate) => candidate !== item));
    debug('remove', item.productId, new Error().stack.split('\n').slice(2, 5).join(' | '));
    announce('remove', item, source);
    return true;
  },

  /**
   * @param {{productId: string|number, variantId?: string|number, handle: string}} entry
   * @param {Element} [source]
   */
  toggle(entry, source) {
    return this.has(entry.productId) ? !this.remove(entry.productId, source) : this.add(entry, source);
  }
};

/** "/en-us/products/some-handle?variant=1" → "some-handle" */
export function handleFromUrl(url) {
  const match = String(url || '').match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function entryFromButton(button) {
  return {
    productId: button.dataset.productId,
    variantId: button.dataset.variantId,
    handle: button.dataset.productHandle || handleFromUrl(button.dataset.productUrl)
  };
}

function syncButton(button, savedIds) {
  const isSaved = savedIds.has(String(button.dataset.productId));
  button.classList.toggle('is-wishlisted', isSaved);
  button.setAttribute('aria-pressed', String(isSaved));
}

function syncAll() {
  const savedIds = new Set(readItems().map((item) => item.productId));
  document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => syncButton(button, savedIds));
  const count = visibleItems().length;
  document.querySelectorAll(COUNT_SELECTOR).forEach((counter) => {
    counter.textContent = count;
    counter.dataset.count = String(count);
    counter.hidden = count === 0;
  });
}

const TOGGLE_GUARD_MS = 400;
const lastToggleAt = new Map();

function debug(...args) {
  try {
    if (localStorage.getItem('tssWishlistDebug')) console.info('[wishlist]', ...args);
  } catch {}
}

function onHeartClick(event) {
  const button = event.target instanceof Element ? event.target.closest(BUTTON_SELECTOR) : null;
  if (!button) return;
  event.preventDefault();

  // One toggle per press: a duplicate click (touch/trackpad) or a second handler
  // would otherwise add and immediately remove the product.
  const productId = String(button.dataset.productId);
  const now = Date.now();
  if (now - (lastToggleAt.get(productId) || 0) < TOGGLE_GUARD_MS) {
    debug('ignored repeat toggle', productId, event.type, event.isTrusted);
    return;
  }
  lastToggleAt.set(productId, now);

  const saved = wishlist.toggle(entryFromButton(button), button);
  debug(saved ? 'added' : 'removed', productId, { trusted: event.isTrusted, items: readItems().length });
}

// Double-tap / double-click a product card to save it, Instagram-style. A single
// tap still opens the product, after a short wait to see if a second one follows.
const DOUBLE_TAP_MS = 250;
let pendingCardTap = null;

function showHeartBurst(card) {
  const media = card.querySelector('.product-card__media') || card;
  const burst = document.createElement('span');
  burst.className = 'product-card__burst';
  burst.setAttribute('aria-hidden', 'true');
  burst.innerHTML =
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M6.979 3.074a6 6 0 0 1 4.988 1.425l.037 .033l.034 -.03a6 6 0 0 1 4.733 -1.44l.246 .036a6 6 0 0 1 3.364 10.008l-.18 .185l-.048 .041l-7.45 7.379a1 1 0 0 1 -1.313 .082l-.094 -.082l-7.493 -7.422a6 6 0 0 1 3.176 -10.215z"/></svg>';
  burst.addEventListener('animationend', () => burst.remove());
  media.append(burst);
}

function onCardLinkClick(event) {
  const link = event.target instanceof Element ? event.target.closest('.product-card__link') : null;
  if (!link) return;
  // Leave keyboard activation, new-tab and modified clicks alone.
  if (event.button !== 0 || event.detail === 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const card = link.closest('.product-card');
  const button = card?.querySelector(BUTTON_SELECTOR);
  if (!button) return;
  event.preventDefault();

  if (pendingCardTap?.link === link) {
    clearTimeout(pendingCardTap.timer);
    pendingCardTap = null;
    if (!wishlist.has(button.dataset.productId)) {
      lastToggleAt.set(String(button.dataset.productId), Date.now());
      wishlist.add(entryFromButton(button), button);
    }
    showHeartBurst(card);
    return;
  }

  if (pendingCardTap) clearTimeout(pendingCardTap.timer);
  pendingCardTap = {
    link,
    timer: setTimeout(() => {
      pendingCardTap = null;
      window.location.assign(link.href);
    }, DOUBLE_TAP_MS)
  };
}

// Bind once per page, even if this module is ever evaluated twice (two URLs).
if (!window.__tssWishlistBound) {
  window.__tssWishlistBound = true;

  // Capture phase: runs even where a card stops propagation (SearchTap), and
  // preventDefault keeps a heart inside a product link from navigating.
  document.addEventListener('click', onHeartClick, true);
  document.addEventListener('click', onCardLinkClick, true);

  // Another tab changed the list.
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      debug('list changed in another tab');
      syncAll();
    }
  });

  // Cards injected later (SearchTap, infinite scroll, section reloads) sync themselves.
  let syncQueued = false;
  new MutationObserver((mutations) => {
    if (syncQueued) return;
    const hasNewButton = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) => node instanceof Element && (node.matches(BUTTON_SELECTOR) || node.querySelector(BUTTON_SELECTOR))
      )
    );
    if (!hasNewButton) return;
    syncQueued = true;
    requestAnimationFrame(() => {
      syncQueued = false;
      syncAll();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
} else {
  debug('module evaluated twice — handlers already bound');
}

syncAll();

// Off the critical path: only ids not yet checked in this market cost a request.
(window.requestIdleCallback || ((callback) => setTimeout(callback, 1500)))(() => refreshAvailability());
